import React, { useEffect, useState } from 'react'
import { Menu, Dropdown, Space } from 'antd'
import SwapOutlined from '@ant-design/icons/SwapOutlined'
import styles from './index.less'
import classNames from 'classnames'
import { getStotageUserData, setStotageUserData } from '@/hooks/useRecordOperation/store'
import { TABLE_SORT } from '@/modules/globals/constants/iknow'

interface sortOption {
    /**
     * @description       字段值
     */
    key: string
    /**
     * @description       显示名称
     */
    label: string
    /**
     * @description       排序方式
     */
    sort?: string | null
    /**
     * @description       是否需要缓存
     */
    cache: boolean
    /**
     * @description       返回的参数
     */
    getParams?: (opt?: any) => void
    /**
     * @description       是否为默认选项
     */
    default?: boolean
    /**
     * @description       需要携带默认查询条件
     */
    needDefaultKey?: boolean
}

interface QuickSortProps {
    /**
     * @description       外层容器类名
     */
    className?: string
    /**
     * @description       配置key
     */
    configKey: string
    /**
     * @description       快捷排序项
     */
    sortOption: Array<sortOption>
    /**
     * @description       更新排序字段
     */
    onChangeQuickSort: (sort: object) => any
    /**
     * @description       用户配置信息
     */
    userConfig: Object
    /**
     * @description       更新用户配置信息
     */
    setUserConfig: () => any
    /**
     * @description       是否平铺显示
     * @default           false
     */
    quickSortExpands?: boolean
}

export const handleConfigKey = (configKey: string) => {
    return configKey ? `${configKey}_SORT` : null
}

/**
 * 表格头部的快捷排序
 * @param {QuickSortProps} QuickSortProps 入参对象
 * @returns {React.Node} 返回内容
 */
const QuickSort: React.FC<QuickSortProps> = ({
    className,
    configKey,
    sortOption,
    quickSortExpands,
    onChangeQuickSort,
    userConfig,
    setUserConfig
}) => {
    const [visible, setVisible] = useState(false)

    const [sortValue, setSortValue] = useState('')
    useEffect(() => {
        // 获取配置里有没有配置过
        const configKeyValue =
            userConfig &&
            handleConfigKey(configKey) &&
            userConfig[handleConfigKey(configKey)] &&
            userConfig[handleConfigKey(configKey)].key

        if (configKeyValue) {
            const storageSortData = getStotageUserData(TABLE_SORT) || {}
            setStotageUserData(TABLE_SORT, {
                ...storageSortData,
                [handleConfigKey(configKey)]: { key: configKeyValue }
            })
        }
        let initValue = null
        const storageSortData = getStotageUserData(TABLE_SORT)
        if (storageSortData && storageSortData[handleConfigKey(configKey)]) {
            initValue = storageSortData[handleConfigKey(configKey)]?.key
        } else {
            initValue = configKeyValue || getDefaultKey()
        }

        // 若是配置项没有发生变化，跳过
        if (initValue !== sortValue) {
            setSortValue(initValue)
            // 返回排序字段
            onChangeQuickSort && onChangeQuickSort(formatBackSort(initValue))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configKey, userConfig])

    // 处理返回参数
    const formatBackSort = (row: string) => {
        // 看配置需不需要携带 defautKey
        const option = getOption(row)

        if (option && option.getParams) {
            return option.getParams(option)
        }
        let rows = [row]
        if (option && option.needDefaultKey) {
            const defautKey = getDefaultKey()
            rows = [...rows, defautKey]
        }
        return rows.reduce((pre, cur) => {
            // 默认情况不传值
            if (cur !== 'default') {
                const keyValue = cur.split('_')
                pre[keyValue[0]] = keyValue[1]
            }
            return pre
        }, {})
    }

    // 保存配置信息
    const handleChange = ({ key }) => {
        setVisible(false)
        if (sortValue === key) {
            return
        }
        const option = getOption(key)
        if (option && option.cache) {
            handleConfigKey(configKey) &&
                setUserConfig({
                    configKey: handleConfigKey(configKey),
                    configValue: { key }
                })
        } else {
            // 不需要缓存的key需要将默认条件也传出
            setSortValue(key)
            onChangeQuickSort && onChangeQuickSort(formatBackSort(key))
        }
    }
    // 根据 key_sort 获取label
    const getLabel = (val: string) => {
        return sortOption
            .filter(cv => {
                return `${cv.key}${cv.sort ? `_${cv.sort}` : ''}` === val
            })
            .map(item => {
                return item.label
            })
            .join()
    }
    // 根据 key_sort 获取对象
    const getOption = (val: string) => {
        return sortOption.filter(cv => {
            return `${cv.key}${cv.sort ? `_${cv.sort}` : ''}` === val
        })?.[0]
    }
    // 获取默认的排序
    const getDefaultKey = () => {
        return sortOption
            .filter(cv => {
                return cv?.default
            })
            .map(item => {
                return `${item.key}${item.sort ? `_${item.sort}` : ''}`
            })
            .join()
    }

    const menu = (
        <Menu onClick={handleChange} selectedKeys={[sortValue]}>
            {sortOption.map(item => {
                return (
                    <Menu.Item key={`${item.key}${item.sort ? `_${item.sort}` : ''}`}>
                        {getLabel(`${item.key}${item.sort ? `_${item.sort}` : ''}`)}
                    </Menu.Item>
                )
            })}
        </Menu>
    )

    if (quickSortExpands) {
        return (
            <Space
                direction="horizontal"
                size={16}
                wrap
                className={classNames(styles.container, styles.quickSortExpands, className)}
            >
                {sortOption.map(item => {
                    const key = `${item.key}${item.sort ? `_${item.sort}` : ''}`
                    return (
                        <a
                            onClick={() => handleChange({ key })}
                            className={classNames({
                                [styles.black]: sortValue !== key
                            })}
                            key={key}
                        >
                            {getLabel(key)}
                        </a>
                    )
                })}
            </Space>
        )
    }

    return (
        <div className={classNames(styles.container, className)}>
            <Dropdown
                overlay={menu}
                trigger={['click']}
                visible={visible}
                onVisibleChange={flag => {
                    setVisible(flag)
                }}
            >
                <a className="ant-dropdown-link" onClick={e => e.preventDefault()}>
                    <SwapOutlined className={styles.icon} />
                    {getLabel(sortValue)}
                </a>
            </Dropdown>
        </div>
    )
}

export default QuickSort
